import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RegulationMetadata } from '@/data/horizonScanningTypes';
import { FileText, Calendar, AlertCircle, Building, Info } from 'lucide-react';
import { toast } from 'sonner';

interface HorizonScanningHeaderProps {
  metadata: RegulationMetadata;
}

export function HorizonScanningHeader({ metadata }: HorizonScanningHeaderProps) {
  const handleReferenceClick = () => {
    toast.info('PDF Viewer', {
      description: 'PDF viewer would open here. This is a placeholder for the actual PDF.',
      duration: 3000,
    });
  };

  const getStatusColor = () => {
    switch (metadata.status) {
      case 'Open for Comments':
        return 'destructive';
      case 'Closed':
        return 'secondary';
      case 'Implemented':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <div className="space-y-6 mb-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{metadata.title}</h1>
          <div className="flex items-center gap-3 mt-2">
            <Badge variant={getStatusColor()} className="flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {metadata.status}
            </Badge>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span className="font-medium">Deadline:</span> {metadata.deadline}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReferenceClick}
              className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
            >
              <FileText className="h-4 w-4 mr-1" />
              Ref: {metadata.reference}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div className="flex items-start gap-2">
            <Info className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <h3 className="font-semibold text-sm text-muted-foreground">Subject</h3>
              <p className="mt-1">{metadata.subject}</p>
            </div>
          </div>
          
          <div className="flex items-start gap-2">
            <Building className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <h3 className="font-semibold text-sm text-muted-foreground">Origin</h3>
              <p className="mt-1">{metadata.origin}</p>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-start gap-2">
            <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <h3 className="font-semibold text-sm text-muted-foreground">Description</h3>
              <p className="mt-1">{metadata.description}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}